import i18n from "../i18n";import { Trans } from "react-i18next";import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  CalendarDays,
  ClipboardCheck,
  Clock,
  Copy,
  ArrowRight,
  KeyRound,
  X,
  CircleUserRound } from
'lucide-react';
import { AppLayout } from '../components/layout';
import sessionDB, { saveSession } from '../utils/sessionDB';
import { QUICK_LINKS } from '../router/navigation';
import { useTranslation } from "react-i18next";

/* ─── Session storage ──────────────────────────────────────────────────
 * WelcomePage used to keep its own private copy of the session (a hand
 * picked subset written to a `wp_session` sessionStorage key). That has
 * been replaced by the shared sessionDB util (src/utils/sessionDB.js),
 * which is now the single place the whole app — this page, utils/api.js,
 * and every merged HRMS/ESS module under src/api, src/lib and
 * src/services — reads and writes the session from.
 * ────────────────────────────────────────────────────────────────────── */
function getSessionView() {
  return {
    tenantCode: sessionDB.getTenantCode(),
    empId: sessionDB.getEmpId(),
    canonicalRoleName: sessionDB.getCanonicalRoleName(),
    accessToken: sessionDB.getAccessToken(),
    refreshToken: sessionDB.getRefreshToken()
  };
}

const CARD =
'bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] border border-white/60';
const PAGE_SUB = 'text-sm text-[var(--color-text-secondary)] mt-1';
const SECTION_LABEL =
'text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider px-5 sm:px-6 pt-5 pb-2';

const QUICK_LINK_ICONS = {
  '/hrms-ess/apply-leave': ClipboardCheck,
  '/hrms-ess/attendance/mark': Clock,
  '/hrms-ess/holidays': Calendar,
  '/hrms-ess/employee-info': CircleUserRound,
  '/hrms-team/leave-requests': CalendarDays
};

/* ─── Toast hook ───────────────────────────────────────── */
function useToast() {
  const [msg, setMsg] = useState('');
  const [visible, setVisible] = useState(false);
  const show = useCallback((text, duration = 2500) => {
    setMsg(text);
    setVisible(true);
    setTimeout(() => setVisible(false), duration);
  }, []);
  return { msg, visible, show };
}

function Toast({ msg, visible }) {
  return (
    <div
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[500] bg-foreground text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-lg transition-opacity pointer-events-none whitespace-nowrap ${
      visible ? 'opacity-100' : 'opacity-0'}`
      }
      role="status"
      aria-live="polite">
      
      {msg}
    </div>);

}

/* ─── Session toggle button (footer) ────────────────────── */
/* ─── Session Info Panel ───────────────────────────────── */
function SessionPanel({ onClose }) {
  const session = getSessionView();
  const [copied, setCopied] = useState('');

  const fields = [
  { label: 'Tenant Code', key: 'tenantCode', value: session.tenantCode },
  { label: 'Employee ID', key: 'empId', value: session.empId },
  { label: 'Canonical Role Name', key: 'canonicalRoleName', value: session.canonicalRoleName },
  { label: 'Access Token', key: 'accessToken', value: session.accessToken, truncate: true },
  { label: 'Refresh Token', key: 'refreshToken', value: session.refreshToken, truncate: true }];


  function handleCopy(key, value) {
    navigator.clipboard.writeText(value || '').then(() => {
      setCopied(key);
      setTimeout(() => setCopied(''), 1500);
    });
  }

  return (
    <>
      <div
        className="fixed inset-0 z-[298]"
        onClick={onClose}
        aria-hidden="true" />
      
      <div className="fixed bottom-[76px] left-1/2 -translate-x-1/2 w-[min(320px,calc(100vw-2rem))] z-[299] bg-white rounded-2xl border border-[var(--color-text-border)]/60 shadow-[0_12px_40px_rgba(30,51,131,0.18)] overflow-hidden animate-in fade-in slide-in-from-bottom-1 duration-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[var(--color-primary)]">
          <div className="flex items-center gap-2 text-sm font-bold text-white">
            <KeyRound className="h-4 w-4" />
            <span><Trans i18nKey="dashboard.sessionInfo" /></span>
          </div>
          <button
            type="button"
            className="h-7 w-7 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 hover:text-white grid place-items-center transition"
            onClick={onClose}
            aria-label={i18n.t("formActions.closeSessionPanel")}>
            
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex flex-col">
          {fields.map((f) =>
          <div
            key={f.key}
            className="px-4 py-2.5 border-b border-[var(--color-text-border)]/40 last:border-b-0 flex flex-col gap-1">
            
              <p className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">
                {f.label}
              </p>
              <div className="flex items-center justify-between gap-2">
                <p
                className={`text-sm font-semibold text-foreground flex-1 ${
                f.truncate ? 'font-mono text-xs text-[var(--color-primary)] truncate max-w-[220px]' : 'break-all'}`
                }>
                
                  {f.value || <span className="text-[var(--color-text-border)] font-normal">—</span>}
                </p>
                {f.value &&
              <button
                type="button"
                className={`h-7 min-w-7 px-1.5 rounded-lg border text-xs font-bold flex items-center justify-center shrink-0 transition ${
                copied === f.key ?
                'bg-emerald-50 text-emerald-600 border-emerald-200' :
                'bg-[var(--color-workspace)]/40 border-[var(--color-text-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] hover:border-[var(--color-primary)]/40'}`
                }
                onClick={() => handleCopy(f.key, f.value)}
                title={i18n.t("formActions.copyToClipboard")}>
                
                    {copied === f.key ? '✓' : <Copy className="h-3.5 w-3.5" />}
                  </button>
              }
              </div>
            </div>
          )}
        </div>
        <p className="m-0 px-4 py-2 text-[10.5px] text-[var(--color-text-secondary)] text-center border-t border-[var(--color-text-border)]/40 bg-[var(--color-workspace)]/20">
          <Trans i18nKey="dashboard.storedInSessionstorageClearedOnTabClose" />
        </p>
      </div>
    </>);

}

/* ─── Main WelcomePage ─────────────────────────────────── */
export default function WelcomePage({ data }) {
  const { t } = useTranslation();
  const emp = data.empobj || {};
  const tenant = sessionDB.getTenantObj();

  const [sessionOpen, setSessionOpen] = useState(false);

  const toast = useToast();
  const navigate = useNavigate();

  /* ── Save session on mount ────────────────────────────── */
  useEffect(() => {
    saveSession(data);
  }, [data]);

  const initials = ((emp.first_name || 'U')[0] + (emp.last_name ? emp.last_name[0] : '')).toUpperCase();

  const overlay =
  <>
      {sessionOpen && <SessionPanel onClose={() => setSessionOpen(false)} />}
      <Toast msg={toast.msg} visible={toast.visible} />
    </>;


  /* ── Content renderer ─────────────────────────────────── */
  function renderContent() {
    const empFields = [
    { label: 'Employee ID', value: emp.emp_id },
    { label: 'Designation', value: emp.designation_name },
    { label: 'Department', value: emp.department_name },
    // { label: 'Grade',       value: emp.grade_level_name },
    //{ label: 'Division',    value: emp.division_name },
    // { label: 'Location',    value: emp.work_location_name },
    { label: 'Location', value: emp.work_location_name }]
    .filter((f) => f.value);
    const today = new Intl.DateTimeFormat('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    }).format(new Date());
    return (
      <div className="max-w-4xl mx-auto w-full flex flex-col gap-5">
          <div className="flex items-center justify-between gap-4">
            <p className={PAGE_SUB}>{t("common.welcome")} {emp.first_name || 'User'}.</p>
            <p className="shrink-0 text-sm text-[var(--color-text-secondary)]">{today}</p>
          </div>

          <section aria-label={i18n.t("dashboard.subscription")}>
            <div className={`${CARD} p-4`}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]"><Trans i18nKey="dashboard.subscription" /></p>
                  <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-foreground">
                    <p><span className="text-[var(--color-text-secondary)]">{t("dateTime.startDate")} : </span> <span className="font-semibold">{tenant.subscriptionStartDate || '—'}</span></p>
                    <p><span className="text-[var(--color-text-secondary)]">{t("dateTime.endDate")} : </span> <span className="font-semibold">{tenant.subscriptionEndDate || '—'}</span></p>
                  </div>
                </div>
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
                  <ClipboardCheck className="h-5 w-5" />
                </span>
              </div>
            </div>
          </section>

          <section className={`${CARD} overflow-hidden`} aria-labelledby="action-centre-heading">
            <div className="flex items-center justify-between px-5 pb-2 pt-5 sm:px-6">
              <div>
                <p id="action-centre-heading" className="text-sm font-bold text-foreground"><Trans i18nKey="dashboard.actionCentre" /></p>
                <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]"><Trans i18nKey="dashboard.commonTasksReadyWhenYouNeedThem" /></p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-2 px-5 pb-5 sm:grid-cols-2 sm:gap-3 sm:px-6 sm:pb-6">
              <button
              type="button"
              onClick={() => navigate('/hrms-ess/attendance/mark')}
              className="group flex items-center gap-3 rounded-2xl border border-[var(--color-text-border)]/60 bg-[var(--color-workspace)]/20 p-4 text-left transition hover:border-[var(--color-primary)]/30 hover:bg-white hover:shadow-[0_10px_24px_-14px_rgba(30,51,131,0.4)]">
              
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[var(--color-primary)]/10 text-[var(--color-primary)] transition-colors group-hover:bg-[var(--color-primary)] group-hover:text-white"><Clock className="h-5 w-5" /></span>
                <span className="min-w-0 flex-1"><span className="block text-sm font-bold text-foreground"><Trans i18nKey="formActions.markAttendance2" /></span><span className="mt-0.5 block text-xs text-[var(--color-text-secondary)]"><Trans i18nKey="dateTime.recordOrReviewTodaySAttendance" /></span></span>
                <ArrowRight className="h-4 w-4 shrink-0 text-[var(--color-text-secondary)] transition group-hover:translate-x-0.5 group-hover:text-[var(--color-primary)]" />
              </button>
              <button
              type="button"
              onClick={() => navigate('/hrms-ess/apply-leave')}
              className="group flex items-center gap-3 rounded-2xl border border-[var(--color-text-border)]/60 bg-[var(--color-workspace)]/20 p-4 text-left transition hover:border-[var(--color-primary)]/30 hover:bg-white hover:shadow-[0_10px_24px_-14px_rgba(30,51,131,0.4)]">
              
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[var(--color-primary)]/10 text-[var(--color-primary)] transition-colors group-hover:bg-[var(--color-primary)] group-hover:text-white"><ClipboardCheck className="h-5 w-5" /></span>
                <span className="min-w-0 flex-1"><span className="block text-sm font-bold text-foreground"><Trans i18nKey="formActions.applyForLeave" /></span><span className="mt-0.5 block text-xs text-[var(--color-text-secondary)]"><Trans i18nKey="dateTime.planTimeOffAndTrackYourRequests" /></span></span>
                <ArrowRight className="h-4 w-4 shrink-0 text-[var(--color-text-secondary)] transition group-hover:translate-x-0.5 group-hover:text-[var(--color-primary)]" />
              </button>
            </div>
          </section>

          <div className={`${CARD} p-5 sm:p-6`}>
            <div className="flex items-center gap-3 pb-4 mb-4 border-b border-[var(--color-text-border)]/50">
              <div className="h-12 w-12 rounded-full bg-[var(--color-primary)] text-white text-lg font-bold grid place-items-center shrink-0">
                {initials}
              </div>
              <div>
                <p className="text-base font-bold text-foreground">
                  {emp.first_name} {emp.last_name || ''}
                </p>
                <p className="text-sm text-[var(--color-text-secondary)] mt-0.5">
                  {emp.canonical_role_name || '—'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {empFields.map((f) =>
            <div key={f.label}>
                  <p className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider mb-0.5">
                    {f.label}
                  </p>
                  <p className="text-sm font-semibold text-foreground">{f.value}</p>
                </div>
            )}
            </div>
          </div>

          <div className={`${CARD} overflow-hidden`}>
            <p className={SECTION_LABEL}><Trans i18nKey="dashboard.quickLinks" /></p>
            <div className="grid grid-cols-1 gap-2 px-5 sm:gap-3 sm:px-6 lg:grid-cols-5 pb-5 sm:pb-6">
              {QUICK_LINKS.map((q) => {
              const Icon = QUICK_LINK_ICONS[q.to] || CalendarDays;
              return (
                <button
                  key={q.to}
                  type="button"
                  className="group flex min-w-0 flex-col items-center gap-2 p-3 rounded-2xl border border-[var(--color-text-border)]/60 bg-[var(--color-workspace)]/20 hover:bg-white hover:shadow-[0_10px_24px_-14px_rgba(30,51,131,0.4)] hover:border-[var(--color-primary)]/30 transition text-center"
                  onClick={() => navigate(q.to)}>
                  
                    <span className="h-10 w-10 rounded-xl bg-[var(--color-primary)]/10 text-[var(--color-primary)] grid place-items-center shrink-0 group-hover:bg-[var(--color-primary)] group-hover:text-white transition-colors">
                      <Icon className="h-5 w-5" />
                    </span>
                    <p className="text-xs font-bold text-foreground truncate w-full">{q.label}</p>
                  </button>);

            })}
            </div>
          </div>

          {/*<SessionFooterToggle sessionOpen={sessionOpen} onSessionToggle={toggleSession} />*/}
        </div>);

  }

  return (
    <AppLayout overlay={overlay} hideBreadcrumb>
      {renderContent()}
    </AppLayout>);

}
